/**
 * PaginationControls Component
 *
 * Responsive pagination controls with desktop and mobile variants.
 * Desktop: Full Mantine Pagination with page numbers
 * Mobile: Simplified prev/next arrows with page indicator
 *
 * @module components/ui/PaginatedList/components/PaginationControls
 */

import { memo } from 'react';
import { Pagination, Text, Group, ActionIcon } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import styles from './PaginationControls.module.css';

interface PaginationControlsProps {
  /** Current page (1-indexed) */
  readonly currentPage: number;
  /** Total number of pages */
  readonly totalPages: number;
  /** Total items count (for display) */
  readonly totalItems?: number;
  /** Page size (for display) */
  readonly pageSize?: number;
  /** Callback when page changes */
  readonly onPageChange: (page: number) => void;
  /** Number of sibling pages to show */
  readonly siblings?: number;
  /** Number of boundary pages */
  readonly boundaries?: number;
  /** Whether loading (disables controls) */
  readonly isLoading?: boolean;
}

function PaginationControlsInner({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  siblings = 1,
  boundaries = 1,
  isLoading = false,
}: PaginationControlsProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  // Don't render if only one page or no pages
  if (totalPages <= 1) {
    return null;
  }

  // Calculate showing range for desktop info
  const showingFrom = totalItems && pageSize ? (currentPage - 1) * pageSize + 1 : undefined;
  const showingTo =
    totalItems && pageSize ? Math.min(currentPage * pageSize, totalItems) : undefined;

  // Mobile: Simplified prev/next controls
  if (isMobile) {
    return (
      <nav
        role="navigation"
        aria-label={t('common.pagination.page')}
        className={styles.container}
      >
        <div className={styles.mobileControls}>
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1 || isLoading}
            aria-label={t('common.pagination.previous')}
            className={styles.navButton}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>

          <Text
            size="sm"
            className={styles.pageIndicator}
            aria-current="page"
            aria-label={t('common.pagination.pageOf', { current: currentPage, total: totalPages })}
          >
            {t('common.pagination.pageIndicator', { current: currentPage, total: totalPages })}
          </Text>

          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || isLoading}
            aria-label={t('common.pagination.next')}
            className={styles.navButton}
          >
            <IconChevronRight size={20} />
          </ActionIcon>
        </div>
      </nav>
    );
  }

  // Desktop: Full Mantine Pagination
  return (
    <nav
      role="navigation"
      aria-label={t('common.pagination.page')}
      className={styles.container}
    >
      <Group gap="md" justify="center" align="center" className={styles.desktopControls}>
        {totalItems !== undefined && showingFrom !== undefined && showingTo !== undefined && (
          <Text size="sm" c="dimmed" className={styles.itemsInfo}>
            {t('common.pagination.showing', {
              from: showingFrom,
              to: showingTo,
              total: totalItems,
            })}
          </Text>
        )}

        <Pagination
          value={currentPage}
          onChange={onPageChange}
          total={totalPages}
          siblings={siblings}
          boundaries={boundaries}
          disabled={isLoading}
          getItemProps={(page) => ({
            'aria-label': t('common.pagination.goToPage', { page }),
          })}
          getControlProps={(control) => {
            const labelMap: Record<string, string> = {
              previous: 'common.pagination.previous',
              next: 'common.pagination.next',
              first: 'common.pagination.first',
              last: 'common.pagination.last',
            };
            const labelKey = labelMap[control];
            return labelKey ? { 'aria-label': t(labelKey) } : {};
          }}
        />

        <Text size="sm" c="dimmed" className={styles.pageInfo}>
          {t('common.pagination.page')} {currentPage} {t('common.pagination.of')} {totalPages}
        </Text>
      </Group>
    </nav>
  );
}

export const PaginationControls = memo(PaginationControlsInner);
